import {
  type City,
  type CityArea,
  type DoctorsListOrder,
  useV1DoctorsListExpandedParallel,
  useV1PremiumDoctorsExpanded,
  useV1SlotsListParallel,
} from "@hermes/api";
import { type Temporal, useLocale, useNow } from "@hermes/intl";
import { useScreenDetector } from "@hermes/ui";
import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useAppConfig } from "#internal/app/config";
import type { GeoPosition } from "#internal/shared/utils";

import { useCity } from "../city";
import { useSlotsDateRanges } from "./date-range";

export type DoctorsListParams = {
  sort?: DoctorsListOrder;
  targetServices?: number[];
  position?: GeoPosition;
  city: City;
  cityArea?: CityArea;
  clinicId?: number;
  enablePromo?: boolean;
  language?: string;
  insurance?: number;
  slotDate?: Temporal.PlainDate;
  nearestCityArea?: number;
  excludedIds?: number[];
  childAge?: number;
  isActive?: boolean;
};

export const useDoctorsListData = ({
  params,
}: {
  params: DoctorsListParams;
}) => {
  const { isRobot } = useAppConfig();
  const { city } = useCity();
  const { isMobile } = useScreenDetector();
  const [locale] = useLocale();
  const now = useNow("day");
  const [seen, setSeen] = useState<boolean[]>([true]);

  useEffect(() => {
    setSeen([true]);
  }, [
    // Important to specify every
    params.city.id,
    params.cityArea?.id,
    params.clinicId,
    params.enablePromo,
    params.insurance,
    params.language,
    params.position?.lat,
    params.position?.lng,
    params.slotDate?.toString(),
    params.sort,
    params.targetServices?.join(","),
    params.nearestCityArea,
    params.excludedIds?.join(","),
    params.isActive,
  ]);

  const limit = isRobot ? 300 : isMobile ? 5 : 8;

  const premiumQuery = useV1PremiumDoctorsExpanded(
    {
      city: city.id,
      service: params.targetServices,
      exclude: params.excludedIds,
    },
    { enabled: params.enablePromo },
  );
  const premiumResults = params.enablePromo
    ? premiumQuery.data?.results.map((data) => ({ ...data, has_slots: true }))
    : undefined;
  const doctorsListParams = {
    limit,
    city: params.city.id,
    city_area: params.cityArea?.id,
    ordering: params.sort,
    service: params.targetServices,
    locale: locale.baseName,
    insurance: params.insurance,
    language: params.language,
    lat: params.position?.lat,
    lng: params.position?.lng,
    slot_date: params.slotDate?.toString(),
    clinic: params.clinicId,
    exclude: params.excludedIds,
    nearest_city_area: params.nearestCityArea,
    age: params.childAge,
    is_active: params.isActive,
  };

  const [firstDoctorsQuery] = useV1DoctorsListExpandedParallel({
    // Always load the first page
    // If cache is intact, it will populate the pages
    pages: [{ seen: true, offset: 0 }],
    ...doctorsListParams,
  });

  const count = firstDoctorsQuery?.data?.count;

  const additionalPages = useMemo(() => {
    if (!count) return [];
    return Array.from(
      { length: Math.ceil(count / limit) - 1 },
      (_, pageIndex) => {
        const offset = (pageIndex + 1) * limit;
        const remaining = count - offset;

        return {
          offset,
          seen: isRobot || (seen[pageIndex + 1] ?? false),
          size: remaining >= limit ? limit : remaining,
        };
      },
    );
  }, [count, limit, seen, isRobot]);
  const pages = [
    {
      seen: true,
      offset: 0,
    },
    ...additionalPages,
  ];

  const otherDoctorsQueries = useV1DoctorsListExpandedParallel({
    pages: additionalPages,
    ...doctorsListParams,
  });

  const doctorsQueries = [firstDoctorsQuery!, ...otherDoctorsQueries];

  const doctorIds = doctorsQueries
    .flatMap((query) => query.data?.results)
    .map((doctor) => doctor?.id)
    .truthy();

  const dateRanges = useSlotsDateRanges({
    targetServices: params.targetServices,
    pages: [
      premiumResults,
      ...doctorsQueries.map((query) =>
        query.data?.results
          .map((doctor) => {
            const services = doctor.services.filter((s) =>
              Boolean(s.nearest_slot_datetime),
            );
            // Exclude doctors with no services with nearest_slot_datetime
            return services.length ? { ...doctor, services } : null;
          })
          .truthy(),
      ),
    ],
    startDate: params.slotDate ?? now.toPlainDate(),
  });

  const slotsQueries = useV1SlotsListParallel(
    { dateRanges },
    {
      placeholderData: keepPreviousData,
      enabled: params.isActive,
    },
  );

  return {
    count,
    doctorIds,
    isInitialFetching: premiumQuery.isFetching || doctorsQueries[0]?.isFetching,
    premiumList: {
      results: premiumResults,
      isFetched: premiumQuery.isFetched,
      isFetching: premiumQuery.isFetching,
    },
    pages: doctorsQueries.map((query, pageIndex) => {
      return {
        seen:
          // If the data is already fetched, we can show the page
          typeof query.data?.results === "undefined"
            ? pages[pageIndex]?.seen
            : true,
        results: query.data?.results
          .map((doctor) => {
            const services = doctor.services.filter((s) =>
              Boolean(s.nearest_slot_datetime),
            );

            // Exclude doctors with no services with nearest_slot_datetime
            return {
              ...doctor,
              has_slots: Boolean(services.length),
            };
          })
          .truthy(),
        isFetching: query.isFetching,
        isFetched: query.isFetched,
      };
    }),
    slotsPages: slotsQueries.map((query) => ({
      results: query.data?.results,
      isFetching: query.isFetching,
      isFetched: query.isFetched,
    })),
    limit,
    fetchPage(index: number) {
      if (!seen[index]) {
        setSeen((seen) => {
          seen[index] = true;
          return [...seen];
        });
      }
    },
  };
};
